import {
	Card,
	Button,
	Table,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Input,
	Select,
	DatePicker,
	notification,
	Badge,
	Tooltip,
	Popconfirm,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	PiCloudDuotone,
	PiEyeDuotone,
	PiMagnifyingGlassDuotone,
	PiFunnelDuotone,
	PiStopCircleDuotone,
	PiTrashDuotone,
	PiHardDrivesDuotone,
	PiPackageDuotone,
	PiMapPinDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiShoppingCartDuotone,
	PiHouseDuotone,
	PiCalendarDuotone,
} from "react-icons/pi";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";
import { requestGetInstanceList } from "../../services/user.service";

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function InstanceListPage() {
	const { teamId } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [instances, setInstances] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		search: searchParams.get("search") || "",
		status: searchParams.get("status") || "",
		serviceType: searchParams.get("serviceType") || "",
		dateRange: null,
	});
	const [suspendLoading, setSuspendLoading] = useState({});
	const [terminateLoading, setTerminateLoading] = useState({});

	useEffect(() => {
		document.title = "Cloudiva App | Instances";
		fetchInstances();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.search) params.set("search", filters.search);
		if (filters.status) params.set("status", filters.status);
		if (filters.serviceType) params.set("serviceType", filters.serviceType);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchInstances = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		if (filters.serviceType) {
			filterParts.push(`serviceType:${filters.serviceType}`);
		}
		// Add more filter fields here as needed

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword || "",
			filter: filterString,
			order: "expiredAt:asc",
		};

		requestGetInstanceList(teamId, params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const instancesData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setInstances(instancesData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching instances:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch instances",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const handleSearch = (value) => {
		setFilters((prev) => ({ ...prev, search: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTerminateInstance = (instanceId) => {
		notification.info({
			message: "Fitur Belum Tersedia",
			description: "Saat ini fitur terminasi layanan belum tersedia",
		});
		// setTerminateLoading((prev) => ({ ...prev, [instanceId]: true }));
		// requestTerminateInstance(instanceId, { reason: "Terminated by admin" })
		// 	.then(() => {
		// 		notification.success({
		// 			message: "Success",
		// 			description: "Instance terminated successfully",
		// 		});
		// 		fetchInstances();
		// 	})
		// 	.catch((error) => {
		// 		console.error("Error terminating instance:", error);
		// 		notification.error({
		// 			message: "Error",
		// 			description: error.response?.data?.message || "Failed to terminate instance",
		// 		});
		// 	})
		// 	.finally(() => {
		// 		setTerminateLoading((prev) => ({ ...prev, [instanceId]: false }));
		// 	});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "ACTIVE":
			case "RUNNING":
				return "green";
			case "SUSPENDED":
				return "red";
			case "TERMINATED":
				return "default";
			default:
				return "blue";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Tertunda";
			case "ACTIVE":
				return "Aktif";
			case "RUNNING":
				return "Sedang Berjalan";
			case "SUSPENDED":
				return "Ditangguhkan";
			case "TERMINATED":
				return "Dihentikan";
			default:
				return status;
		}
	};

	const columns = [
		{
			title: "Nomor",
			dataIndex: "number",
			key: "number",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiCloudDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							ID: {record._id.substring(0, 8)}...
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Layanan",
			dataIndex: ["service", "name"],
			key: "service",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							Code: {record.service?.code} • Type: {record.type}
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Paket",
			dataIndex: ["plan", "name"],
			key: "plan",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						{record.plan?.price?.amount && formatCurrency(record.plan.price.amount)}/
						{record.plan?.price?.interval}
					</div>
				</div>
			),
		},
		// {
		// 	title: "Region",
		// 	dataIndex: ["region", "name"],
		// 	key: "region",
		// 	width: 120,
		// 	render: (text, record) => (
		// 		<div style={{ display: "flex", alignItems: "center" }}>
		// 			<PiMapPinDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
		// 			<div>
		// 				<div style={{ fontWeight: 500 }}>{text || "Global"}</div>
		// 				{record.region?.code && (
		// 					<div style={{ fontSize: "12px", color: "#666" }}>{record.region.code}</div>
		// 				)}
		// 			</div>
		// 		</div>
		// 	),
		// },
		{
			title: "Tim",
			dataIndex: ["team", "name"],
			key: "team",
			render: (text, record) =>
				text ? (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiUserDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
						<div>
							<div style={{ fontWeight: 500 }}>{text}</div>
							<div style={{ fontSize: "12px", color: "#666" }}>
								{record.team?.owner?.email?.substring(0, 20)}...
							</div>
						</div>
					</div>
				) : (
					<Text type="secondary">-</Text>
				),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status, record) => (
				<Space size={2} direction="vertical">
					<Tag color={getStatusColor(status)} style={{ borderRadius: "12px" }}>
						{getStatusText(status)}
					</Tag>
					{dayjs(record.expiredAt).isBefore(dayjs().add(60, "day")) && (
						<Tag color="#ff4d4f" style={{ borderRadius: "12px" }}>
							Habis dalam {dayjs(record.expiredAt).diff(dayjs(), "day")} hari
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Tanggal",
			key: "dates",
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#666" }} />
						<span>Terdaftar: {dayjs(record.registeredAt).format("MMM DD, YYYY")}</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#ff7a45" }} />
						<span
							style={{
								color: dayjs(record.expiredAt).isBefore(dayjs().add(30, "day"))
									? "#ff4d4f"
									: "#666",
							}}>
							Kedaluwarsa: {dayjs(record.expiredAt).format("MMM DD, YYYY")}
						</span>
					</div>
					{record.nextBilledAt && (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiReceiptDuotone size={12} style={{ marginRight: 4, color: "#1890ff" }} />
							<span>Tagihan Berikutnya: {dayjs(record.nextBilledAt).format("MMM DD, YYYY")}</span>
						</div>
					)}
				</div>
			),
		},
		{
			title: "Aksi",
			key: "actions",
			width: 80,
			fixed: "right",
			align: "right",
			render: (_, record) => (
				<Space>
					<Tooltip title="Lihat Detail">
						<Button
							type="text"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/${teamId}/instances/${record._id}`)}
						/>
					</Tooltip>
					{record.status === "ACTIVE" && (
						<Tooltip title="Hentikan Layanan">
							<Popconfirm
								title="Hentikan Layanan"
								description="Tindakan ini tidak dapat dibatalkan. Apakah Anda yakin ingin menghentikan layanan ini?"
								onConfirm={() => handleTerminateInstance(record._id)}
								okText="Ya, Hentikan"
								cancelText="Batal">
								<Button
									type="text"
									danger
									icon={<PiTrashDuotone />}
									loading={terminateLoading[record._id]}
								/>
							</Popconfirm>
						</Tooltip>
					)}
				</Space>
			),
		},
	];

	const expandedRowRender = (record) => {
		return (
			<div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "8px" }}>
				<Row gutter={24}>
					<Col span={8}>
						<div style={{ marginBottom: "12px" }}>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Data Terkait
							</Text>
							<Space direction="vertical" size="small">
								{record.orderId && (
									<Button
										type="link"
										size="small"
										icon={<PiShoppingCartDuotone />}
										onClick={() => navigate(`${teamId}/orders/${record.orderId}`)}>
										Lihat Pesanan
									</Button>
								)}
								{record.invoiceId && (
									<Button
										type="link"
										size="small"
										icon={<PiReceiptDuotone />}
										onClick={() => navigate(`${teamId}/invoices/${record.invoiceId}`)}>
										Lihat Faktur
									</Button>
								)}
							</Space>
						</div>
					</Col>
					<Col span={8}>
						<div style={{ marginBottom: "12px" }}>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Detail Layanan
							</Text>
							<div style={{ fontSize: "12px" }}>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Kode Layanan: </Text>
									<Text>{record.service?.code}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Kode Paket: </Text>
									<Text>{record.plan?.code}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Tipe: </Text>
									<Tag size="small">{record.type}</Tag>
								</div>
							</div>
						</div>
					</Col>
					<Col span={8}>
						<div>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Timestamp
							</Text>
							<div style={{ fontSize: "12px" }}>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Dibuat: </Text>
									<Text>{dayjs(record.createdAt).format("DD MMM YYYY, HH:mm")}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Diperbarui: </Text>
									<Text>{dayjs(record.updatedAt).format("DD MMM YYYY, HH:mm")}</Text>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		);
	};

	return (
		<div className="page-content">
			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							Layanan Anda
							<Badge count={pagination.total} showZero></Badge>
						</Space>
					}
					extra={
						<Space>
							<Search
								placeholder="Cari layanan..."
								allowClear
								value={filters.search}
								enterButton={<PiMagnifyingGlassDuotone />}
								style={{ width: 250 }}
								onSearch={handleSearch}
							/>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<Select
								placeholder="Filter dg status"
								allowClear
								value={filters.status || undefined}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("status", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Select.Option value="PENDING">Tertunda</Select.Option>
								<Select.Option value="ACTIVE">Aktif</Select.Option>
								<Select.Option value="RUNNING">Berjalan</Select.Option>
								<Select.Option value="SUSPENDED">Ditangguhkan</Select.Option>
								<Select.Option value="TERMINATED">Dihentikan</Select.Option>
							</Select>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter dg tipe layanan"
								allowClear
								value={filters.serviceType || undefined}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("serviceType", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Select.Option value="WEHO">Web Hosting</Select.Option>
								<Select.Option value="APLA.AP">App Deployment</Select.Option>
								<Select.Option value="APLA.DB">Database Deployment</Select.Option>
								<Select.Option value="APLA.SV">Service Deployment</Select.Option>
							</Select>
						</Col>
						<Col span={6}>
							<RangePicker
								style={{ width: "100%" }}
								onChange={(dates) => handleFilterChange("dateRange", dates)}
								placeholder={["Tanggal Awal", "Tanggal Akhir"]}
							/>
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={instances}
						rowKey="_id"
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} layanan`,
						}}
						onChange={handleTableChange}
						expandable={{
							expandedRowRender,
							rowExpandable: (record) => true,
						}}
						scroll={{ x: 1200 }}
						size="small"
					/>
				</Card>
			</div>
		</div>
	);
}

export default InstanceListPage;
